import React, { useEffect, useState } from 'react';

interface DeleteModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title?: string;
  subtitle?: string;
  itemName?: string;
  warningText?: string;
  isLoading?: boolean;
  confirmButtonText?: string;
  itemType?: string; // e.g., "Article", "Publication", "Post"
}

export const DeleteModal: React.FC<DeleteModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  title,
  subtitle,
  warningText,
  isLoading = false,
  confirmButtonText = 'Delete',
  itemType = 'Article'
}) => {
  const [isAnimating, setIsAnimating] = useState(false);

  useEffect(() => {
    if (isOpen) {
      // Trigger animation after mount
      setTimeout(() => setIsAnimating(true), 10);
    } else {
      setIsAnimating(false);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const defaultTitle = title || `Delete ${itemType}?`;
  const defaultSubtitle = subtitle || `Are you sure you want to delete this ${itemType.toLowerCase()}?`;
  const defaultWarningText = warningText || 'You will lose all content.';

  return (
    <div 
      className={`fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 transition-opacity duration-300 ${
        isAnimating ? 'opacity-100' : 'opacity-0'
      }`}
      onClick={onClose}
    >
      <div 
        className={`bg-white rounded-xl shadow-xl max-w-md w-full mx-4 overflow-hidden transition-all duration-300 ease-out ${
          isAnimating ? 'opacity-100 scale-100 translate-y-0' : 'opacity-0 scale-95 translate-y-4'
        }`}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Illustration Header */}
        <div className="bg-gray-100 p-6 flex items-center justify-center">
          <img 
            src="/delete-icon.png" 
            alt="Delete" 
            className="w-full h-auto max-h-48 object-contain" 
          />
        </div>

        {/* Content Area */}
        <div className="p-6">
          <h3 className="text-xl font-bold text-gray-900 mb-2">{defaultTitle}</h3>
          <p className="text-sm text-gray-600 mb-1">{defaultSubtitle}</p>
          <p className="text-sm text-gray-600 mb-6">{defaultWarningText || 'They will lose all access.'}</p>

          {/* Action Buttons */}
          <div className="flex gap-3">
            <button
              onClick={onConfirm}
              disabled={isLoading}
              className="flex-1 px-4 py-2 text-sm font-semibold text-white bg-[#FF6B6B] rounded-lg hover:bg-[#FF5252] disabled:opacity-50 transition-colors"
            >
              {isLoading ? 'Deleting...' : confirmButtonText}
            </button>
            <button
              onClick={onClose}
              disabled={isLoading}
              className="flex-1 px-4 py-2 text-sm font-semibold text-white bg-gray-600 rounded-lg hover:bg-gray-700 disabled:opacity-50 transition-colors"
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DeleteModal;

